import React, { useContext, useEffect, useRef, useState } from 'react';
import setuLogo from '../assets/setu-logo-small.svg';
import cssClass from './EditorCanvasHeader.module.css';
import { UserContext } from '../context/UserContext';
import { useNavigate, useParams } from 'react-router-dom';
import RoadmapTitleEditor from './roadmap-utility/RoadmapTitleEditor';
import { emitCustomEvent } from '../utils/helper';
import { RoadmapContext } from '../context/RoadmapContext';
import saveIcon from '../assets/roadmap-control-panel/save-icon.svg';
import previewIcon from '../assets/roadmap-control-panel/preview-icon.svg';
import publishIcon from '../assets/roadmap-control-panel/publish-icon.svg';
import manageAccessIcon from '../assets/roadmap-control-panel/manage-access-icon.svg';
import dashboardIcon from '../assets/roadmap-control-panel/dashboard-icon.svg';
import pushEmailIcon from '../assets/roadmap-control-panel/push-email-icon.svg';
import RoadmapAccessSharing from './RoadmapAccessSharing';
import { AnimatePresence } from 'framer-motion';
import ContentUpdateMailerModal from './modals/ContentUpdateMailerModal';

/**
 * EditorCanvasHeader Component
 *
 * This component displays the application's header, including the logo, navigation,
 * user authentication controls, and a dropdown user details card.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} [props.style] - Additional styles to apply to the header.
 * @returns {JSX.Element} The rendered Header component.
 */
const EditorCanvasHeader = () => {
  const {
    roadmapVisibility,
    setRoadmapVisibility,
    roadmapStatus,
    setRoadmapSatus,
    roadmapTitle,
    roadmapAccess,
    isSavingRoadmap,
  } = useContext(RoadmapContext);

  // State for displaying user details dropdown
  const [showUserDetailsCard, setShowUserDetailsCard] = useState(false);
  const [showManageAccessModal, setShowManageAccessModal] = useState(false);
  const [showContentUpdateMailerModal, setShowContentUpdateMailerModal] = useState(false);
  const [currentRoadmap, setCurrentRoadmap] = useState({});
  // Refs for detecting clicks outside the dropdown
  const userLogoRef = useRef(null);
  const userDetailsCardRef = useRef(null);

  const { roadmap_id } = useParams();

  // Hook for navigation
  const navigate = useNavigate();

  /**
   * Closes the user details dropdown.
   */
  const closeUserDetailsCard = () => setShowUserDetailsCard(false);
  const onCancleManageAccess = () => {
    setShowManageAccessModal(false);
  };
  const onCancleContentUpdateMailer = () => {
    setShowContentUpdateMailerModal(false);
  };

  const handleSaveClick = () => {
    emitCustomEvent('roadmap::update', {});
  };

  const handleNavigateToDashboard = () => {
    navigate('/');
  };

  const handleKeyDown = (event) => {
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      emitCustomEvent('roadmap::update', {});
    }
  };

  const handlePreviewButtonClick = () => {
    window.open(`/preview/${roadmap_id}`, '_blank');
  };

  const handleRoadmapPublish = () => {
    const newVisibility = roadmapVisibility === 'public' ? 'private' : 'public';
    const newStatus = roadmapStatus === 'draft' ? 'published' : 'draft';
    setRoadmapVisibility(newVisibility);
    setRoadmapSatus(newStatus);
    emitCustomEvent('roadmap::update', {
      visibility: newVisibility,
      status: newStatus,
      change: 'visibility',
    });
  };

  const handleManageAccessClick = () => {
    setShowManageAccessModal(true);
  };

  const handleContentUpdateMailerClick = () => {
    setShowContentUpdateMailerModal(true);
  };

  /**
   * Detects clicks outside the user details dropdown and closes it.
   * Runs when `showUserDetailsCard` changes.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDetailsCardRef.current &&
        !userDetailsCardRef.current.contains(event.target) &&
        userLogoRef.current &&
        !userLogoRef.current.contains(event.target)
      ) {
        closeUserDetailsCard();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDetailsCard]);

  /**
   * Set the current roadmap to the manage access modal to provide access for the roadmap and get roadmap share data
   * Runs when `roadmapAccess`, `roadmap_id`, `roadmapTitle` changes.
   */
  useEffect(() => {
    setCurrentRoadmap({
      r_id: roadmap_id,
      title: roadmapTitle,
      owner: roadmapAccess === 'owner',
    });
  }, [roadmapAccess, roadmap_id, roadmapTitle]);

  return (
    <>
      <div className={cssClass['header']}>
        <div className={cssClass['logo']}>
          <a href="/">
            <img src={setuLogo} alt="logo" className={cssClass['logo-img']} />
          </a>
          {/* Roadmap Title Editor */}
          <RoadmapTitleEditor roadmapId={roadmap_id} />
        </div>
        <div className={cssClass['actions']}>
          <div className={cssClass['util-div']} onClick={handleSaveClick}>
            <img src={saveIcon} alt="" />
            <div>{isSavingRoadmap ? 'Saving...' : 'Save'}</div>
          </div>
          <div
            className={cssClass['util-div']}
            onClick={handlePreviewButtonClick}
          >
            <img src={previewIcon} alt="" />
            <div>Preview</div>
          </div>
          <div className={cssClass['util-div']} onClick={handleRoadmapPublish}>
            <img src={publishIcon} alt="" />
            <div>
              {roadmapStatus === 'published' ? 'Unpublish' : 'Publish'}
            </div>
          </div>
          <div
            className={`${cssClass['util-div']} ${cssClass['manage-access']}`}
            onClick={handleManageAccessClick}
          >
            <img src={manageAccessIcon} alt="" />
            <div>Manage Access</div>
          </div>
          <div
            className={cssClass['util-div']}
            onClick={handleNavigateToDashboard}
          >
            <img src={dashboardIcon} alt="" />
            <div>Dashboard</div>
          </div>
          <div
            className={cssClass['util-div']}
            onClick={handleContentUpdateMailerClick}
          >
            <img src={pushEmailIcon} alt="" />
            <div>Push Content Updates</div>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {showManageAccessModal && (
          <RoadmapAccessSharing
            source="canvas"
            currentRoadmap={currentRoadmap}
            onCancel={onCancleManageAccess}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showContentUpdateMailerModal && (
          <ContentUpdateMailerModal onCancel={onCancleContentUpdateMailer} />
        )}
      </AnimatePresence>
    </>
  );
};

export default EditorCanvasHeader;
